import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { ConditionalLink } from "components/conditionalLink";
import { INKLE_WEBSITE_URL } from "constants/base";

const SALES_TAX_REGISTRATION_HELP_LINK = `${INKLE_WEBSITE_URL}sales-tax/register-for-sales-tax-on-inkle`;

type SalesTaxComplianceBannersProps = {
  showExposedBanner: boolean;
  showApproachingBanner: boolean;
  showCartPendingBanner: boolean;
  showPendingRegistrationBanner: boolean;
  showFilingInProgressBanner: boolean;
  stateName?: string;
  isRegisteringNow?: boolean;
  onDismissApproachingBanner: () => void;
  onRegisterNow: () => void;
  onAlreadyRegistered: () => void;
  onGoToCart: () => void;
  onViewFiling: () => void;
};

export const SalesTaxComplianceBanners = ({
  showExposedBanner,
  showApproachingBanner,
  showCartPendingBanner,
  showPendingRegistrationBanner,
  showFilingInProgressBanner,
  stateName,
  isRegisteringNow,
  onDismissApproachingBanner,
  onRegisterNow,
  onAlreadyRegistered,
  onGoToCart,
  onViewFiling,
}: SalesTaxComplianceBannersProps) => {
  return (
    <>
      {showExposedBanner && (
        <Banner
          type="warning"
          title="Possible sales tax compliance risk"
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <span>
                {`Our records show you've crossed the registration threshold in ${stateName}, but aren't registered yet. Register now to stay compliant. `}
                <ConditionalLink
                  to={SALES_TAX_REGISTRATION_HELP_LINK}
                  className="t-text-text-30 !t-underline"
                >
                  Learn how it works
                </ConditionalLink>
              </span>

              <div className="t-flex t-items-center t-gap-3">
                <Button
                  customType="secondary"
                  size="xsmall"
                  type="button"
                  isLoading={isRegisteringNow}
                  disabled={isRegisteringNow}
                  onClick={onRegisterNow}
                >
                  Register now
                </Button>
                <Button
                  customType="link_neutral"
                  size="xsmall"
                  type="button"
                  disabled={isRegisteringNow}
                  onClick={onAlreadyRegistered}
                >
                  I've already registered
                </Button>
              </div>
            </div>
          }
        />
      )}
      {showApproachingBanner && (
        <Banner
          type="warning"
          title="You're approaching the sales tax registration threshold"
          onClose={onDismissApproachingBanner}
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <span>
                {`Your business is approaching the sales tax registration threshold in ${stateName}. Starting your registration now will help you stay ahead of upcoming compliance requirements. `}
                <ConditionalLink
                  to={SALES_TAX_REGISTRATION_HELP_LINK}
                  className="t-text-text-30 !t-underline"
                >
                  Learn how it works
                </ConditionalLink>
              </span>
              <div className="t-flex t-items-center t-gap-3">
                <Button
                  customType="secondary"
                  size="small"
                  type="button"
                  isLoading={isRegisteringNow}
                  disabled={isRegisteringNow}
                  onClick={onRegisterNow}
                >
                  Register now
                </Button>
                <Button
                  customType="link_neutral"
                  size="small"
                  type="button"
                  disabled={isRegisteringNow}
                  onClick={onAlreadyRegistered}
                >
                  I've already registered
                </Button>
              </div>
            </div>
          }
        />
      )}
      {showCartPendingBanner && (
        <Banner
          type="warning"
          title={`Complete your ${stateName} registration`}
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <span>
                {`Your ${stateName} sales tax registration is in your cart. Complete payment to start processing. `}
                <ConditionalLink
                  to={SALES_TAX_REGISTRATION_HELP_LINK}
                  className="t-text-text-30 !t-underline"
                >
                  Learn how it works
                </ConditionalLink>
              </span>
              <div className="t-flex t-items-center t-gap-3">
                <Button
                  customType="secondary"
                  size="small"
                  type="button"
                  onClick={onGoToCart}
                >
                  Go to cart
                </Button>
                <Button
                  customType="link_neutral"
                  size="small"
                  type="button"
                  onClick={onAlreadyRegistered}
                >
                  I've already registered
                </Button>
              </div>
            </div>
          }
        />
      )}
      {showFilingInProgressBanner && (
        <Banner
          type="neutral"
          title="Registration in progress"
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <span>
                {`We're working on your ${stateName} sales tax registration. Check its progress anytime or let us know if you've already registered. `}
                <ConditionalLink
                  to={SALES_TAX_REGISTRATION_HELP_LINK}
                  className="t-text-text-30 !t-underline"
                >
                  Learn how it works
                </ConditionalLink>
              </span>
              <div className="t-flex t-items-center t-gap-3">
                <Button
                  customType="secondary"
                  size="small"
                  type="button"
                  onClick={
                    showFilingInProgressBanner ? onViewFiling : onGoToCart
                  }
                >
                  Check status
                </Button>
                <Button
                  customType="link_neutral"
                  size="small"
                  type="button"
                  onClick={onAlreadyRegistered}
                >
                  I've already registered
                </Button>
              </div>
            </div>
          }
        />
      )}
    </>
  );
};
